import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { IconButton, styled } from '@mui/material';
import { LiveHelp } from '@mui/icons-material';

const AnimatedIcon = styled(LiveHelp)(({ theme }) => ({
  animation: "shake 1s infinite",
  "@keyframes shake": {
    "0%": { transform: "translate(0, 0)" },
    "25%": { transform: "translate(0, -2px)" },
    "50%": { transform: "translate(0, 2px)" },
    "75%": { transform: "translate(0, -2px)" },
    "100%": { transform: "translate(0, 0)" }
  }
}));

export default function Faq() {
  const [open, setOpen] = React.useState(false);
  const [scroll, setScroll] = React.useState('paper');

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const descriptionElementRef = React.useRef(null);
  React.useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  return (
    <>
      <IconButton color="inherit" onClick={handleClickOpen('paper')}>
        <AnimatedIcon fontSize="inherit" />
      </IconButton>
      <React.Fragment>
        <Dialog
          open={open}
          onClose={handleClose}
          scroll={scroll}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          maxWidth="700px"
        >
          <DialogTitle id="scroll-dialog-title">FAQ for SOLA Procurement Vendor Registration</DialogTitle>
          <DialogContent dividers={scroll === 'paper'}>
            <DialogContentText
              id="scroll-dialog-description"
              ref={descriptionElementRef}
              tabIndex={-1}
              width='700px'
            >
              <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', margin: 0 }}>
                <p><strong>Question 1:</strong> I am unable to register in the system, system states that my company already registered. What do I do?</p>
                <p><strong>Answer:</strong> The system allows accounts with 2 access levels. Admin access is automatically granted to the first registered user, with full access to vendor and RFQ details. All other users receive standard access. Enter your company VOEN/TaxID and contact details to proceed with user registration.</p>

                <p><strong>Question 2:</strong> I need to update my company details, how do I make changes?</p>
                <p><strong>Answer:</strong> Log in using your admin account. Go to the Vendor tab and click the Revise option. Changes trigger a GL validation process. Your account will display old details until approved. You will receive system notifications at each step.</p>

                <p><strong>Question 3:</strong> Can my company be invited and participate in bids if approval mail is not received?</p>
                <p><strong>Answer:</strong> No, your company must be approved by GL to be invited into bids.</p>

                <p><strong>Question 4:</strong> Who from my organization can view and update vendor-related information?</p>
                <p><strong>Answer:</strong> There are two types of access: Admin and Regular. Only one user can have admin access, which is required to update vendor details.</p>

                <p><strong>Question 5:</strong> I cannot progress to the next step of the evaluation form. System flags fields in red even when details are added. What do I do?</p>
                <p><strong>Answer:</strong> After entering each field, press ENTER to validate your entry before proceeding.</p>

                <p><strong>Question 6:</strong> I am trying to include an attachment in the evaluation form, but the system shows an error. What do I do?</p>
                <p><strong>Answer:</strong> Make sure your files are in doc, ppt, pdf, exe, or jpg format and under 50MB.</p>

                <p><strong>Question 7:</strong> My company Tax ID is different from what I see in the system. What do I do?</p>
                <p><strong>Answer:</strong> Contact <a href="mailto:sola@gl.world">sola@gl.world</a>. Include your company name and VOEN/TaxID.</p>

                <p><strong>Question 8:</strong> What do I enter under Representative Company field?</p>
                <p><strong>Answer:</strong> Enter all companies for which you have distributorship or agreements allowing you to supply products/services.</p>

                <p><strong>Question 9:</strong> I am unable to find my product category in the list. What do I do?</p>
                <p><strong>Answer:</strong> Contact <a href="mailto:sola@gl.world">sola@gl.world</a>. Include your company name and VOEN/TaxID.</p>

                <p><strong>Question 10:</strong> How do I know if my registration was successful?</p>
                <p><strong>Answer:</strong> Once submitted, your request triggers a system notification for GL review. You will receive an approval or rejection notification. The review may take up to 10 working days.</p>

                <p><strong>Question 11:</strong> Whom can I contact for support?</p>
                <p><strong>Answer:</strong> Contact <a href="mailto:sola@gl.world">sola@gl.world</a> and include your company name and VOEN/TaxID.</p>

                <p><strong>Question 12:</strong> How long does registration approval take?</p>
                <p><strong>Answer:</strong> GL review may take up to 10 working days. You will be notified once the request is reviewed.</p>

                <p><strong>Question 13:</strong> What documents are required for registration?</p>
                <p><strong>Answer:</strong> Several documents are required for your company to be added to the AVL (approved vendor list). Follow system steps to upload the necessary documents.</p>

                <p><strong>Question 14:</strong> What does "Pending" / "In Review" status mean on my registration dashboard?</p>
                <p><strong>Answer:</strong> After submitting your request, GL review starts. This status remains until the process is complete. The same applies if your company details are revised.</p>

                <h2>FAQ for SOLA Procurement RFQ</h2>

                <p><strong>Question 1:</strong> Why am I unable to amend RFQs in the system?</p>
                <p><strong>Answer:</strong> Only active RFQs are open for changes. You cannot modify an RFQ after the submission deadline.</p>

                <p><strong>Question 2:</strong> Who from my organization can access or respond to RFQs?</p>
                <p><strong>Answer:</strong> Admin users (only one per company) have access to vendor and RFQ details. Regular users can only access the Procurement section. Both can view and respond to RFQs.</p>

                <p><strong>Question 3:</strong> Can I update RFQ details or prices after submission?</p>
                <p><strong>Answer:</strong> Yes, you can revise submissions before the RFQ deadline.</p>

                <p><strong>Question 4:</strong> Can I attach files when responding to an RFQ?</p>
                <p><strong>Answer:</strong> Yes, attachments are allowed when submitting an RFQ response.</p>

                <p><strong>Question 5:</strong> What happens if I miss the RFQ deadline? Can I still submit a quote?</p>
                <p><strong>Answer:</strong> No, you cannot submit responses after the RFQ deadline.</p>
              </div>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    </>

  );
}